import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, Search, X,
  Loader2, Trash2, BarChart3, Table as TableIcon, FileDown,
  Pencil, Check, Filter, FileSpreadsheet,
} from 'lucide-react';
import { DateRangePresets, computePresetRange } from '@/components/Service/DateRangePresets';
import { PageSizeNav } from '@/components/Service/PageSizeNav';
import { BulkEditModal } from '@/components/Service/BulkEditModal';
import { useUploadGuard } from '@/components/UploadGuard';

import { fmtINR, inputCx } from './Reconciliation/reconUtils';
import { CellInput, StatusPill } from './Reconciliation/CellInput';
import { TechnicianMultiFilter } from './Reconciliation/TechnicianMultiFilter';
import { UploadCard } from './Reconciliation/UploadCard';
import { SumTile, BigMetric } from './Reconciliation/MetricTiles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReconciliationTab({ user }) {
  const [view, setView] = React.useState('table');
  const { ensureCompanyId, guardModal } = useUploadGuard(user);

  const [activePreset, setActivePreset] = React.useState('last7');
  const [dateFrom, setDateFrom] = React.useState(() => computePresetRange('last7')[0]);
  const [dateTo, setDateTo] = React.useState(() => computePresetRange('last7')[1]);
  const [sortDir, setSortDir] = React.useState('asc');

  const [filters, setFilters] = React.useState({ job_no: '', particulars: '', status: '', voucher_no: '' });
  const [techMulti, setTechMulti] = React.useState([]);
  const [outstandingFilter, setOutstandingFilter] = React.useState('');

  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [metrics, setMetrics] = React.useState({
    spares_sales_total: 0, labour_charges_total: 0, ss_gross_total: 0,
    rt_gross_total: 0, outstanding_total: 0, row_count: 0,
  });

  const [uploadingSS, setUploadingSS] = React.useState(false);
  const [uploadingRT, setUploadingRT] = React.useState(false);

  const [pageSize, setPageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);

  const [selected, setSelected] = React.useState(new Set());
  const [bulkOpen, setBulkOpen] = React.useState(false);

  // Top horizontal scroll mirror
  const topScrollRef = React.useRef(null);
  const tableScrollRef = React.useRef(null);
  const [tableWidth, setTableWidth] = React.useState(0);

  React.useEffect(() => {
    const t = tableScrollRef.current;
    if (!t) return;
    const ro = new ResizeObserver(() => {
      const inner = t.querySelector('table');
      if (inner) setTableWidth(inner.scrollWidth);
    });
    ro.observe(t);
    return () => ro.disconnect();
  }, [rows.length, view]);

  const onTopScroll = (e) => { if (tableScrollRef.current) tableScrollRef.current.scrollLeft = e.target.scrollLeft; };
  const onTableScroll = (e) => { if (topScrollRef.current) topScrollRef.current.scrollLeft = e.target.scrollLeft; };

  const fetchReport = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (techMulti.length) params.set('technicians', techMulti.join(','));
      if (outstandingFilter) params.set('outstanding_filter', outstandingFilter);
      params.set('sort_dir', sortDir);
      const { data } = await axios.get(`${API}/service/reconciliation/report?${params}`);
      setRows(data.rows || []);
      setMetrics(data.metrics || metrics);
      setSelected(new Set());
    } catch {
      toast.error('Failed to load reconciliation report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filters, sortDir, techMulti, outstandingFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => { fetchReport(); }, [fetchReport]);
  React.useEffect(() => { setPage(1); }, [pageSize, dateFrom, dateTo, filters, sortDir, techMulti, outstandingFilter]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const paged = rows.slice((page - 1) * pageSize, page * pageSize);

  const techOptions = React.useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.technician) s.add(r.technician); });
    return [...s].sort();
  }, [rows]);

  const onUpload = async (sheetType, file) => {
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error('Please upload an Excel (.xlsx / .xls) file');
      return;
    }
    const targetCo = await ensureCompanyId();
    if (!targetCo) { toast.info('Upload cancelled — no target company selected'); return; }
    const setUp = sheetType === 'SS' ? setUploadingSS : setUploadingRT;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target_company_id', targetCo);
      const { data } = await axios.post(
        `${API}/service/reconciliation/upload?sheet_type=${sheetType}`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const skipped = data.skipped_existing_job_no ? ` · ${data.skipped_existing_job_no} duplicate Job No skipped` : '';
      toast.success(`${sheetType}: ${data.imported} new rows imported${skipped}`);
      fetchReport();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${sheetType} upload failed`);
    } finally {
      setUp(false);
    }
  };

  const onClear = async (sheetType) => {
    if (!window.confirm(
      `Clear ALL ${sheetType} reconciliation entries?\n\n` +
      `This will remove only the uploaded ${sheetType} Excel data — ` +
      `Service Job Cards will remain intact.`
    )) return;
    try {
      await axios.delete(`${API}/service/reconciliation/clear?sheet_type=${sheetType}`);
      toast.success(`${sheetType} reconciliation data cleared`);
      fetchReport();
    } catch {
      toast.error('Clear failed');
    }
  };

  const clearFilters = () => {
    setFilters({ job_no: '', particulars: '', status: '', voucher_no: '' });
    setTechMulti([]);
    setOutstandingFilter('');
    setActivePreset('last7');
    const [a, b] = computePresetRange('last7');
    setDateFrom(a); setDateTo(b);
  };

  const togglePick = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const ids = paged.map((r) => r.id);
      const allSel = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSel) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  // Inline edit state
  const [editingId, setEditingId] = React.useState(null);
  const [editDraft, setEditDraft] = React.useState({});

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({
      ss_date: row.ss_date || '', ss_voucher_no: row.ss_voucher_no || '',
      particulars: row.particulars || '',
      spares_sales: row.spares_sales || 0, labour_charges: row.labour_charges || 0,
      ss_gross_total: row.ss_gross_total || 0,
      rt_id: row.rt_id, rt_date: row.rt_date || '', rt_voucher_no: row.rt_voucher_no || '',
      rt_gross_total: row.rt_gross_total || 0,
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const saveEdit = async (row) => {
    try {
      await axios.put(`${API}/service/reconciliation/${row.id}`, {
        date: editDraft.ss_date,
        voucher_no: editDraft.ss_voucher_no,
        particulars: editDraft.particulars,
        spares_sales: Number(editDraft.spares_sales) || 0,
        labour_charges: Number(editDraft.labour_charges) || 0,
        gross_total: Number(editDraft.ss_gross_total) || 0,
      });
      if (row.rt_id) {
        await axios.put(`${API}/service/reconciliation/${row.rt_id}`, {
          date: editDraft.rt_date,
          voucher_no: editDraft.rt_voucher_no,
          gross_total: Number(editDraft.rt_gross_total) || 0,
        });
      }
      toast.success('Saved');
      cancelEdit();
      fetchReport();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(
      `Clear the SS/RT reconciliation entry for Job #${row.job_no}?\n\n` +
      `This will only remove the uploaded SS and RT amounts for this Job No — ` +
      `the Service Job Card itself will REMAIN INTACT.`
    )) return;
    try {
      await axios.delete(`${API}/service/reconciliation/row/${row.id}`);
      if (row.rt_id) {
        await axios.delete(`${API}/service/reconciliation/row/${row.rt_id}`).catch(() => {});
      }
      toast.success('Reconciliation data cleared (Job Card preserved)');
      fetchReport();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(
      `Clear ${selected.size} selected reconciliation entries?\n\n` +
      `Only the uploaded SS and RT amounts will be removed — ` +
      `the underlying Service Job Cards will remain intact.`
    )) return;
    try {
      const ssIds = [...selected];
      const rtIds = rows.filter((r) => selected.has(r.id) && r.rt_id).map((r) => r.rt_id);
      const allIds = [...ssIds, ...rtIds];
      const { data } = await axios.post(`${API}/service/reconciliation/bulk-delete`, { ids: allIds });
      toast.success(`${data.deleted} reconciliation entry(ies) cleared (Job Cards preserved)`);
      fetchReport();
    } catch {
      toast.error('Bulk delete failed');
    }
  };

  const handleBulkUpdate = async (updates) => {
    if (!Object.keys(updates).length) { toast.error('Nothing to update'); return; }
    try {
      const { data } = await axios.post(`${API}/service/reconciliation/bulk-update`, { ids: [...selected], updates });
      toast.success(`${data.modified} row(s) updated`);
      setBulkOpen(false);
      fetchReport();
    } catch {
      toast.error('Bulk update failed');
    }
  };

  const handleExport = async (fmtType) => {
    const target = selected.size ? rows.filter((r) => selected.has(r.id)) : rows;
    if (!target.length) { toast.error('No rows to export'); return; }
    const slim = target.map((r, i) => ({
      S_No: i + 1,
      Job_No: r.job_no || '',
      Technician: r.technician || '',
      Vehicle_In: `${r.vehicle_in_date || ''} ${r.vehicle_in_time || ''}`.trim(),
      Status: r.status || '',
      SS_Date: r.ss_date || '', SS_Voucher: r.ss_voucher_no || '',
      Particulars: r.particulars || '',
      Spares_Sales: r.spares_sales || 0,
      Labour_Charges: r.labour_charges || 0,
      SS_Gross_Total: r.ss_gross_total || 0,
      RT_Date: r.rt_date || '', RT_Voucher: r.rt_voucher_no || '',
      RT_Gross_Total: r.rt_gross_total || 0,
      Outstanding: r.outstanding || 0,
    }));
    try {
      const res = await axios.post(
        `${API}/service/reports/export`,
        { rows: slim, format: fmtType, report_name: 'Reconciliation Statement' },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reconciliation_Statement.${fmtType === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${fmtType.toUpperCase()} downloaded`);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="reconciliation-tab">
      {guardModal}
      {/* Upload Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UploadCard
          title="SS Excel"
          subtitle="Sales / Service entries — Job No, Date, Voucher No, Particulars, Spares Sales, Labour Charges, Gross Total"
          uploading={uploadingSS}
          onChoose={(f) => onUpload('SS', f)}
          onClear={() => onClear('SS')}
          testIdPrefix="ss"
          accent="sky"
        />
        <UploadCard
          title="RT Excel"
          subtitle="Receipts / Payments — Job No, Date, Voucher No, Gross Total"
          uploading={uploadingRT}
          onChoose={(f) => onUpload('RT', f)}
          onClear={() => onClear('RT')}
          testIdPrefix="rt"
          accent="violet"
        />
      </div>

      {/* Sum totals at top */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SumTile label="Spares Sales" value={metrics.spares_sales_total} accent="sky" testId="metric-spares" />
        <SumTile label="Labour Charges" value={metrics.labour_charges_total} accent="violet" testId="metric-labour" />
        <SumTile label="Total Revenue" value={metrics.ss_gross_total} accent="emerald" testId="metric-revenue" />
        <SumTile label="Receipts" value={metrics.rt_gross_total} accent="amber" testId="metric-receipts" />
        <SumTile label="Outstanding" value={metrics.outstanding_total} accent={metrics.outstanding_total > 0 ? 'rose' : 'emerald'} testId="metric-outstanding" highlight />
      </div>

      {/* Date range + filters */}
      <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-4">
        <DateRangePresets
          activePreset={activePreset} setActivePreset={setActivePreset}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          sortDir={sortDir} setSortDir={setSortDir}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-3 pt-3 border-t border-slate-100">
          {[
            ['job_no', 'Job No'],
            ['particulars', 'Particulars'],
            ['status', 'Status'],
            ['voucher_no', 'SS Voucher No'],
          ].map(([k, label]) => (
            <div key={k} className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={filters[k]}
                onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value }))}
                placeholder={label}
                data-testid={`recon-filter-${k}`}
                className={`${inputCx} pl-7`}
              />
              {filters[k] && (
                <button onClick={() => setFilters((f) => ({ ...f, [k]: '' }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Multi-filters row */}
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100">
          <span className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium inline-flex items-center gap-1.5">
            <Filter size={12} /> Filters
          </span>
          <TechnicianMultiFilter options={techOptions} selected={techMulti} onChange={setTechMulti} />
          <select
            value={outstandingFilter}
            onChange={(e) => setOutstandingFilter(e.target.value)}
            data-testid="recon-outstanding-filter"
            className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[12.5px] text-slate-700"
          >
            <option value="">All payment states</option>
            <option value="with">Outstanding (&gt; 0)</option>
            <option value="without">Settled (= 0)</option>
            <option value="overpaid">Overpaid (&lt; 0)</option>
          </select>
          <button onClick={clearFilters} className="text-[12px] text-sky-600 hover:text-sky-800 inline-flex items-center gap-1">
            <X size={12} /> Clear
          </button>
          <span className="flex-1" />
          <button onClick={fetchReport} data-testid="recon-refresh" className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-[12.5px] hover:bg-slate-50 inline-flex items-center gap-1.5">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={() => handleExport('excel')} data-testid="recon-export-excel" className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-[12.5px] hover:bg-slate-50 inline-flex items-center gap-1.5">
            <FileSpreadsheet size={13} /> Excel
          </button>
          <button onClick={() => handleExport('pdf')} data-testid="recon-export-pdf" className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-[12.5px] hover:bg-slate-50 inline-flex items-center gap-1.5">
            <FileDown size={13} /> PDF
          </button>
        </div>
      </div>

      {/* View tabs + bulk bar */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 px-1.5 py-1.5 rounded-xl bg-white/70 border border-white/50 w-fit">
          <button
            onClick={() => setView('table')}
            data-testid="recon-view-table"
            className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-all ${view === 'table' ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-600 hover:text-sky-700'}`}
          >
            <TableIcon size={13} /> Table
          </button>
          <button
            onClick={() => setView('metrics')}
            data-testid="recon-view-metrics"
            className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-all ${view === 'metrics' ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-600 hover:text-sky-700'}`}
          >
            <BarChart3 size={13} /> Metrics
          </button>
        </div>
        {selected.size > 0 && view === 'table' && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sky-50 border border-sky-200">
            <Check size={14} className="text-sky-600" />
            <span className="text-[12.5px] text-sky-900 font-medium">{selected.size} selected</span>
            <button onClick={() => setBulkOpen(true)} data-testid="recon-bulk-edit" className="px-2.5 py-1 rounded-md bg-white text-sky-700 border border-sky-200 text-[12px] font-medium hover:bg-sky-50 flex items-center gap-1">
              <Pencil size={11} /> Edit
            </button>
            <button onClick={handleBulkDelete} data-testid="recon-bulk-delete" className="px-2.5 py-1 rounded-md bg-rose-600 text-white text-[12px] font-medium hover:bg-rose-700 flex items-center gap-1">
              <Trash2 size={11} /> Delete
            </button>
            <button onClick={() => setSelected(new Set())} className="px-1 text-slate-500 hover:text-slate-700"><X size={12} /></button>
          </div>
        )}
        <span className="flex-1" />
        {view === 'table' && (
          <PageSizeNav
            pageSize={pageSize} onPageSizeChange={setPageSize}
            page={page} totalPages={totalPages} onPageChange={setPage}
            testIdPrefix="recon-pagesize"
          />
        )}
      </div>

      {/* Top horizontal scroll mirror */}
      {view === 'table' && rows.length > 0 && (
        <div className="rounded-xl bg-white/70 backdrop-blur border border-white/50 px-2 py-1">
          <div className="overflow-x-auto" ref={topScrollRef} onScroll={onTopScroll} data-testid="recon-top-scroll">
            <div style={{ width: tableWidth || '100%', height: 1 }} />
          </div>
        </div>
      )}

      {view === 'table' ? (
        <div className="rounded-2xl bg-white/75 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden">
          <div className="overflow-auto max-h-[70vh] relative" ref={tableScrollRef} onScroll={onTableScroll}>
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 z-20">
                <tr className="text-left bg-sky-600 text-white">
                  <th className="p-2 w-9 bg-sky-600">
                    <input
                      type="checkbox"
                      checked={paged.length > 0 && paged.every((r) => selected.has(r.id))}
                      onChange={toggleAllOnPage}
                      data-testid="recon-select-all"
                      className="w-4 h-4 rounded border-white/40 text-sky-700 bg-white/90"
                    />
                  </th>
                  <th className="p-2 font-semibold w-[44px] bg-sky-600">S.No</th>
                  <th className="p-2 font-semibold w-[78px] bg-sky-600">Job No</th>
                  <th className="p-2 font-semibold w-[120px] bg-sky-600">Technician</th>
                  <th className="p-2 font-semibold w-[110px] bg-sky-600">Vehicle In</th>
                  <th className="p-2 font-semibold w-[100px] bg-sky-600">Status</th>
                  <th className="p-2 font-semibold w-[88px] bg-sky-700">SS Date</th>
                  <th className="p-2 font-semibold w-[100px] bg-sky-700">SS Vch</th>
                  <th className="p-2 font-semibold w-[160px] bg-sky-700">Particulars</th>
                  <th className="p-2 font-semibold w-[100px] bg-sky-700 text-right">Spares</th>
                  <th className="p-2 font-semibold w-[100px] bg-sky-700 text-right">Labour</th>
                  <th className="p-2 font-semibold w-[110px] bg-sky-700 text-right">SS Gross</th>
                  <th className="p-2 font-semibold w-[88px] bg-violet-700">RT Date</th>
                  <th className="p-2 font-semibold w-[100px] bg-violet-700">RT Vch</th>
                  <th className="p-2 font-semibold w-[110px] bg-violet-700 text-right">RT Gross</th>
                  <th className="p-2 font-semibold w-[120px] bg-rose-600 text-right">Outstanding</th>
                  <th className="p-2 font-semibold w-[90px] sticky right-0 bg-sky-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={17} className="p-12 text-center"><Loader2 size={22} className="animate-spin text-sky-500 mx-auto" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={17} className="p-12 text-center text-slate-400 text-[13px]">
                      No reconciliation entries. Upload SS &amp; RT Excel files above to get started.
                    </td>
                  </tr>
                ) : paged.map((r, idx) => {
                  const sno = (page - 1) * pageSize + idx + 1;
                  const isSel = selected.has(r.id);
                  const isEditing = editingId === r.id;
                  const baseTone = idx % 2 === 0 ? 'bg-white' : 'bg-sky-50/70';
                  const stickyTone = idx % 2 === 0 ? 'bg-white' : 'bg-sky-50';
                  const outstandingPositive = (r.outstanding || 0) > 0;
                  return (
                    <tr key={r.id} className={`border-b border-sky-100 ${isSel ? 'bg-sky-100/80' : `${baseTone} hover:bg-sky-100/40`}`}>
                      <td className="p-2 align-top">
                        <input type="checkbox" checked={isSel} onChange={() => togglePick(r.id)} className="w-4 h-4 rounded border-slate-300 text-sky-600" data-testid={`recon-row-${r.id}`} />
                      </td>
                      <td className="p-2 align-top text-slate-500">{sno}</td>
                      <td className="p-2 align-top font-semibold text-sky-700">{r.job_no}</td>
                      <td className="p-2 align-top text-slate-700 truncate" title={r.technician || ''}>{r.technician || '—'}</td>
                      <td className="p-2 align-top text-slate-600 whitespace-nowrap">
                        {r.vehicle_in_date || '—'}{r.vehicle_in_time ? ` ${r.vehicle_in_time}` : ''}
                      </td>
                      <td className="p-2 align-top"><StatusPill value={r.status} /></td>

                      {isEditing ? (
                        <>
                          <td className="p-1 bg-sky-50/30"><CellInput value={editDraft.ss_date} onChange={(v) => setEditDraft((d) => ({ ...d, ss_date: v }))} /></td>
                          <td className="p-1 bg-sky-50/30"><CellInput value={editDraft.ss_voucher_no} onChange={(v) => setEditDraft((d) => ({ ...d, ss_voucher_no: v }))} /></td>
                          <td className="p-1 bg-sky-50/30"><CellInput value={editDraft.particulars} onChange={(v) => setEditDraft((d) => ({ ...d, particulars: v }))} /></td>
                          <td className="p-1 bg-sky-50/30"><CellInput value={editDraft.spares_sales} onChange={(v) => setEditDraft((d) => ({ ...d, spares_sales: v }))} numeric /></td>
                          <td className="p-1 bg-sky-50/30"><CellInput value={editDraft.labour_charges} onChange={(v) => setEditDraft((d) => ({ ...d, labour_charges: v }))} numeric /></td>
                          <td className="p-1 bg-sky-50/50"><CellInput value={editDraft.ss_gross_total} onChange={(v) => setEditDraft((d) => ({ ...d, ss_gross_total: v }))} numeric /></td>
                          <td className="p-1 bg-violet-50/30"><CellInput value={editDraft.rt_date} onChange={(v) => setEditDraft((d) => ({ ...d, rt_date: v }))} disabled={!r.rt_id} /></td>
                          <td className="p-1 bg-violet-50/30"><CellInput value={editDraft.rt_voucher_no} onChange={(v) => setEditDraft((d) => ({ ...d, rt_voucher_no: v }))} disabled={!r.rt_id} /></td>
                          <td className="p-1 bg-violet-50/50"><CellInput value={editDraft.rt_gross_total} onChange={(v) => setEditDraft((d) => ({ ...d, rt_gross_total: v }))} numeric disabled={!r.rt_id} /></td>
                        </>
                      ) : (
                        <>
                          <td className="p-2 align-top text-slate-500 bg-sky-50/30 whitespace-nowrap">{r.ss_date || '—'}</td>
                          <td className="p-2 align-top text-slate-700 bg-sky-50/30 font-mono">{r.ss_voucher_no || '—'}</td>
                          <td className="p-2 align-top text-slate-700 bg-sky-50/30 truncate" title={r.particulars || ''}>{r.particulars || '—'}</td>
                          <td className="p-2 align-top text-right text-slate-800 bg-sky-50/30">{fmtINR(r.spares_sales)}</td>
                          <td className="p-2 align-top text-right text-slate-800 bg-sky-50/30">{fmtINR(r.labour_charges)}</td>
                          <td className="p-2 align-top text-right text-slate-900 bg-sky-50/50 font-semibold">{fmtINR(r.ss_gross_total)}</td>
                          <td className="p-2 align-top text-slate-500 bg-violet-50/30 whitespace-nowrap">{r.rt_date || '—'}</td>
                          <td className="p-2 align-top text-slate-700 bg-violet-50/30 font-mono">{r.rt_voucher_no || '—'}</td>
                          <td className="p-2 align-top text-right text-slate-900 bg-violet-50/50 font-semibold">{fmtINR(r.rt_gross_total)}</td>
                        </>
                      )}

                      <td className={`p-2 align-top text-right font-semibold ${outstandingPositive ? 'bg-rose-50/80 text-rose-700' : 'bg-emerald-50/80 text-emerald-700'}`}>
                        {fmtINR(r.outstanding)}
                      </td>

                      <td className={`p-2 align-top text-right whitespace-nowrap sticky right-0 ${stickyTone}`}>
                        {isEditing ? (
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => saveEdit(r)} title="Save" data-testid={`recon-save-${r.id}`} className="p-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                              <Check size={13} />
                            </button>
                            <button onClick={cancelEdit} title="Cancel" className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
                              <X size={13} />
                            </button>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => startEdit(r)} title="Edit" data-testid={`recon-edit-${r.id}`} className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-sky-700 hover:border-sky-200 hover:bg-sky-50">
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => handleDelete(r)} title="Delete" data-testid={`recon-delete-${r.id}`} className="p-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="recon-metrics-view">
          <BigMetric title="Spares Sales" value={metrics.spares_sales_total} accent="sky" />
          <BigMetric title="Labour Charges" value={metrics.labour_charges_total} accent="violet" />
          <BigMetric title="Total Revenue" value={metrics.ss_gross_total} accent="emerald" subtitle="Sum of SS Gross Totals" />
          <BigMetric title="Receipts" value={metrics.rt_gross_total} accent="amber" subtitle="Sum of RT Gross Totals" />
          <BigMetric title="Outstanding Balance" value={metrics.outstanding_total} accent={metrics.outstanding_total > 0 ? 'rose' : 'emerald'} subtitle="SS Gross − RT Gross" highlight />
          <BigMetric title="Records" value={metrics.row_count} accent="slate" subtitle="Reconciled entries" plain />
        </div>
      )}

      <BulkEditModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        count={selected.size}
        onApply={handleBulkUpdate}
        fields={[
          { key: 'voucher_no', label: 'Voucher No' },
          { key: 'date', label: 'Date (DD-MM-YYYY)' },
          { key: 'particulars', label: 'Particulars' },
        ]}
      />
    </div>
  );
}
